import React, { useState, useEffect } from 'react';
import { DollarSign, History, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { productsService } from '@/services/products.service';
import type { ProductPresentation, PresentationPriceHistory } from '../types/product.types';

interface PresentationPriceHistoryModalProps {
  isOpen: boolean;
  presentation: ProductPresentation | null;
  productName: string;
  onClose: () => void;
  onSavePrice: (presentation: ProductPresentation, price: number) => void;
}

export const PresentationPriceHistoryModal: React.FC<PresentationPriceHistoryModalProps> = ({
  isOpen,
  presentation,
  productName,
  onClose,
  onSavePrice
}) => {
  const [priceHistory, setPriceHistory] = useState<PresentationPriceHistory[]>([]);
  const [newPrice, setNewPrice] = useState(0);
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadPriceHistory = async () => {
    if (!presentation) return;
    
    setLoading(true);
    try {
      const history = await productsService.getPresentationPriceHistoryById(presentation.id);
      setPriceHistory(history);
    } catch (error) {
      console.error('Error loading price history:', error);
      setPriceHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (presentation) {
      setNewPrice(presentation.price || 0);
      loadPriceHistory();
    }
  }, [presentation]);

  const currentPrice = presentation?.price || 0;
  const priceChange = newPrice - currentPrice;
  const priceChangePercent = currentPrice > 0 ? (priceChange / currentPrice) * 100 : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPrice || newPrice <= 0 || !presentation) {
      return;
    }

    setLoading(true);
    try {
      await productsService.updatePresentationById(presentation.id, {
        ...presentation,
        price: newPrice
      });

      // Call the onSavePrice callback to refresh the parent component
      await onSavePrice(presentation, newPrice);
      
      // Reload the price history to show the new record
      await loadPriceHistory();
      
      // Reset form
      setReason('');
      onClose();
    } catch (error) {
      console.error('Error updating price:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[85vh] w-[90vw] min-w-[800px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-macartur-primary" />
            Gestión de Precios - {productName} ({presentation?.name})
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* New Price Form */}
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Precio Actual</h3>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(currentPrice)}
              </div>
              <div className="text-sm text-gray-500">
                Presentación: {presentation?.name}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="newPrice">Nuevo Precio *</Label>
                <Input
                  id="newPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPrice}
                  onChange={(e) => setNewPrice(parseFloat(e.target.value) || 0)}
                  required
                />
                {priceChange !== 0 && (
                  <div className="mt-2 text-sm">
                    <span className={priceChange > 0 ? 'text-red-600' : 'text-green-600'}>
                      {priceChange > 0 ? '+' : ''}{formatCurrency(priceChange)} 
                      ({priceChangePercent > 0 ? '+' : ''}{priceChangePercent.toFixed(1)}%)
                    </span>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="effectiveDate">Fecha de Vigencia *</Label>
                <Input
                  id="effectiveDate"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  required
                />
                <div className="text-xs text-gray-500 mt-1">
                  Fecha desde la cual será efectivo el nuevo precio
                </div>
              </div>

              <div>
                <Label htmlFor="reason">Motivo del Cambio</Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: Actualización de costos, promoción, etc."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !newPrice || newPrice <= 0}
                  className="bg-macartur-primary hover:bg-macartur-primary/90"
                >
                  {loading ? 'Guardando...' : 'Actualizar Precio'}
                </Button>
              </div>
            </form>
          </div>

          {/* Price History */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-gray-500" />
              <h3 className="font-medium text-gray-900">Historial de Precios</h3>
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {priceHistory.map((record, index) => (
                <div key={record.id} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-gray-900">
                      {formatCurrency(record.price)}
                    </div>
                    {index === 0 && (
                      <Badge variant="outline" className="text-xs">
                        Actual
                      </Badge>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-3 h-3" />
                      Creado: {formatDate(record.createdAt)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-600 font-medium">
                      <Calendar className="w-3 h-3" />
                      Vigente desde: {new Date(record.effectiveDate).toLocaleDateString('es-PE')}
                    </div>
                  </div>
                  
                  {record.reason && (
                    <div className="text-sm text-gray-600">
                      {record.reason}
                    </div>
                  )}
                  
                  {index > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      Cambio: {((record.price - priceHistory[index - 1].price) / priceHistory[index - 1].price * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              ))}
              
              {priceHistory.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p>No hay historial de precios disponible</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};